import type { CSSProperties, JSX } from 'react';
import { hexWithAlpha, type Palette } from '../../../_theme/palette.js';
import type { SurveyTheme } from '../types.js';

interface BrandingFooterProps {
  palette: Palette;
  theme: SurveyTheme | undefined;
  hideBranding?: boolean;
}

// Survey footer with the platform "Powered by ChirpBack" credit only.
// The end-user brand (logo + name) renders at the TOP of the survey via
// <BrandHeader/> so the SDK matches the public hosted page.
//
// - `appearance.hideBranding` removes the entire footer
// - `branding.showFooter: false` removes the powered-by line
// - `branding.footerText` overrides the platform credit string verbatim
export function BrandingFooter({ palette, theme, hideBranding }: BrandingFooterProps): JSX.Element | null {
  if (hideBranding) return null;
  const showPoweredBy = theme?.branding?.showFooter !== false;
  if (!showPoweredBy) return null;

  const customPoweredText = theme?.branding?.footerText?.trim();

  return (
    <footer style={footerStyle(palette)}>
      {customPoweredText
        ? <span style={{ color: palette.textMuted }}>{customPoweredText}</span>
        : <PoweredByPill palette={palette} />}
    </footer>
  );
}

function PoweredByPill({ palette }: { palette: Palette }): JSX.Element {
  return (
    <span style={pillStyle(palette)}>
      <span aria-hidden style={pillDotStyle(palette)} />
      <span style={{ opacity: 0.85 }}>Powered by</span>
      <span style={pillBrandStyle(palette)}>ChirpBack</span>
    </span>
  );
}

function footerStyle(palette: Palette): CSSProperties {
  return {
    padding: '12px 20px 14px',
    fontSize: 11,
    color: palette.textMuted,
    borderTop: `1px solid ${hexWithAlpha(palette.border, 0.7)}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    flexShrink: 0,
  };
}

function pillStyle(palette: Palette): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    border: `1px solid ${hexWithAlpha(palette.border, 0.65)}`,
    backgroundColor: hexWithAlpha(palette.textMuted, 0.04),
    color: palette.textMuted,
    fontSize: 11,
    letterSpacing: '0.01em',
    lineHeight: 1,
  };
}

function pillDotStyle(palette: Palette): CSSProperties {
  return {
    display: 'inline-block',
    height: 5,
    width: 5,
    borderRadius: 999,
    backgroundColor: palette.primary,
    boxShadow: `0 0 0 2px ${hexWithAlpha(palette.primary, 0.18)}`,
  };
}

function pillBrandStyle(palette: Palette): CSSProperties {
  return {
    fontWeight: 600,
    color: palette.text,
    letterSpacing: '-0.01em',
  };
}
